import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { DrawerV2, ButtonV2, ChipV2, dsV2 } from '@bloomays-lib/ui.shared';
import { internationalPhoneRegex, mailRegex, nameRegex } from '@bloomays-lib/utils.shared';

import { FieldV2, InputV2, SelectV2, Row2, InfoV2 } from '../formFields';
import type { ContactSlot } from '../../../../helpers/missionDashboardV2';
import { useContactMutations, type ContactSearchResult, type ContactLinkTarget } from './useContactMutations';

export type ContactAddDrawerProps = {
  open: boolean;
  onClose: () => void;
  slot: ContactSlot;
  missionId: string;
  /** Société cible pour une liaison niveau société (société cliente ou fournisseur). */
  societyId?: string;
  societyName?: string;
  onDone?: () => void | Promise<unknown>;
};

type LinkLevel = 'mission' | 'society';
type AddMode = 'search' | 'create';

const NAME_ERROR = 'Min 1 lettre, sans emoji ni caractère spécial';
const MAIL_ERROR = 'Format requis : xxxx@xx.xx';
const PHONE_ERROR = 'Format requis : 0xxxxxxxxx ou +xxxxxxxxxxx';

const contactLabel = (c: ContactSearchResult) =>
  c.fullname || `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.email || 'Contact';

/**
 * Ajout d'un contact à un emplacement (§8) : soit recherche dans l'annuaire (`searchContacts`) puis
 * liaison, soit création d'un nouveau contact (`upsertContact`) puis liaison. La liaison se fait au
 * niveau mission (prioritaire) ou société (repli partagé), selon les niveaux autorisés par le slot.
 */
export const ContactAddDrawer = ({
  open,
  onClose,
  slot,
  missionId,
  societyId,
  societyName,
  onDone,
}: ContactAddDrawerProps) => {
  const { search, searchResults, searching, busy, linkToMission, linkToSociety, createAndLink } = useContactMutations({
    missionId,
    onDone,
  });

  const [mode, setMode] = useState<AddMode>('search');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ContactSearchResult | null>(null);

  // Champs de création.
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [nameJob, setNameJob] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  const canSociety = slot.allowSociety && Boolean(societyId) && Boolean(slot.societyRole);
  const bothLevels = slot.allowMission && canSociety;
  const canLink = slot.allowMission || canSociety;
  const [level, setLevel] = useState<LinkLevel>(slot.allowMission ? 'mission' : 'society');

  const trimmed = {
    firstname: firstname.trim(),
    lastname: lastname.trim(),
    email: email.trim(),
    nameJob: nameJob.trim(),
    phoneNumber: phoneNumber.trim(),
  };
  const createErrors = {
    firstname: trimmed.firstname.length < 1 || !nameRegex.test(trimmed.firstname) ? NAME_ERROR : undefined,
    lastname: trimmed.lastname.length < 1 || !nameRegex.test(trimmed.lastname) ? NAME_ERROR : undefined,
    email: trimmed.email.length < 1 || !mailRegex.test(trimmed.email) ? MAIL_ERROR : undefined,
    phoneNumber: trimmed.phoneNumber && !internationalPhoneRegex.test(trimmed.phoneNumber) ? PHONE_ERROR : undefined,
  };
  const createInvalid = Object.values(createErrors).some(Boolean) || trimmed.nameJob.length < 1;

  const linkTarget = (): ContactLinkTarget =>
    level === 'society' && societyId && slot.societyRole
      ? { level: 'society', societyId, role: slot.societyRole }
      : { level: 'mission', role: slot.missionRole, side: slot.side };

  const submit = async () => {
    if (!canLink) return;
    let ok = false;
    if (mode === 'create') {
      if (createInvalid) return;
      ok = await createAndLink(
        {
          firstname: trimmed.firstname,
          lastname: trimmed.lastname,
          email: trimmed.email,
          nameJob: trimmed.nameJob,
          phoneNumber: trimmed.phoneNumber || undefined,
        },
        linkTarget(),
      );
    } else {
      if (!selected) return;
      const target = linkTarget();
      ok =
        target.level === 'society'
          ? await linkToSociety(selected.recordId, target.societyId, target.role)
          : await linkToMission(selected.recordId, target.role, target.side);
    }
    if (ok) onClose();
  };

  // Recherche annuaire à la frappe (debounce 300 ms, min 2 caractères) — pas de bouton.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const handle = setTimeout(() => void search(q), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const submitDisabled = !canLink || (mode === 'create' ? createInvalid : !selected);

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title="Ajouter un contact"
      subtitle={`${slot.side === 'supplier' ? 'Fournisseur' : 'Client'} · ${slot.label}`}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            Annuler
          </ButtonV2>
          <ButtonV2
            onClick={submit}
            disabled={submitDisabled}
            loading={busy}
            startIcon={<PersonAddAlt1RoundedIcon sx={{ fontSize: 15 }} />}
          >
            {mode === 'create' ? 'Créer et lier' : 'Lier le contact'}
          </ButtonV2>
        </>
      }
    >
      {/* Bascule recherche / création */}
      <Box sx={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {(['search', 'create'] as AddMode[]).map((m) => {
          const active = mode === m;
          return (
            <Box
              key={m}
              component="button"
              type="button"
              onClick={() => setMode(m)}
              sx={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: dsV2.radius.sm,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${active ? dsV2.color.bleu : dsV2.color.border}`,
                background: active ? dsV2.color.bleuDim : dsV2.color.blanc,
                color: active ? dsV2.color.bleu : dsV2.color.t3,
              }}
            >
              {m === 'search' ? 'Rechercher' : 'Créer'}
            </Box>
          );
        })}
      </Box>

      {mode === 'search' ? (
        <>
          <FieldV2 label="Rechercher dans l'annuaire" hint="Nom, prénom ou email (min. 2 caractères).">
            <InputV2 value={query} onChange={setQuery} placeholder="Nom, email…" ariaLabel="contact-search-input" />
          </FieldV2>

          <Box sx={{ marginBottom: '14px' }}>
            {query.trim().length < 2 ? (
              <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                Tapez au moins 2 caractères pour rechercher.
              </Box>
            ) : searching ? (
              <Box sx={{ fontSize: 12, color: dsV2.color.t4, padding: '8px 0' }}>Recherche…</Box>
            ) : searchResults.length === 0 ? (
              <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
                Aucun contact trouvé — basculez sur « Créer ».
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: 260, overflowY: 'auto' }}>
                {searchResults.map((contact) => {
                  const active = selected?.recordId === contact.recordId;
                  return (
                    <Box
                      key={contact.recordId}
                      role="button"
                      onClick={() => setSelected(contact)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '9px 11px',
                        cursor: 'pointer',
                        borderRadius: dsV2.radius.sm,
                        border: `1px solid ${active ? dsV2.color.bleu : dsV2.color.border}`,
                        background: active ? dsV2.color.bleuDim : dsV2.color.blanc,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>
                          {contactLabel(contact)}
                        </Box>
                        {contact.email ? (
                          <Box
                            sx={{ fontSize: 11, color: dsV2.color.t4, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {contact.email}
                            {contact.nameJob ? ` · ${contact.nameJob}` : ''}
                          </Box>
                        ) : null}
                      </Box>
                      {active ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: dsV2.color.bleu }} /> : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          <Row2>
            <FieldV2 label="Prénom" required error={createErrors.firstname}>
              <InputV2 value={firstname} onChange={setFirstname} placeholder="Prénom" />
            </FieldV2>
            <FieldV2 label="Nom" required error={createErrors.lastname}>
              <InputV2 value={lastname} onChange={setLastname} placeholder="Nom" />
            </FieldV2>
          </Row2>
          <FieldV2 label="Email" required error={createErrors.email}>
            <InputV2 type="email" value={email} onChange={setEmail} placeholder="prenom.nom@exemple.com" />
          </FieldV2>
          <FieldV2 label="Fonction" required>
            <InputV2 value={nameJob} onChange={setNameJob} placeholder="Ex. Directeur financier" />
          </FieldV2>
          <FieldV2 label="Téléphone" error={createErrors.phoneNumber}>
            <InputV2 type="tel" value={phoneNumber} onChange={setPhoneNumber} placeholder="Optionnel" />
          </FieldV2>
        </>
      )}

      {!canLink ? (
        <InfoV2 tone="amber">Aucune cible de liaison disponible pour cet emplacement.</InfoV2>
      ) : bothLevels ? (
        <FieldV2
          label="Niveau de liaison"
          hint="Mission : prioritaire pour cette mission. Société : repli partagé par toutes les missions de la société."
        >
          <SelectV2
            value={level}
            onChange={(v) => setLevel(v as LinkLevel)}
            options={[
              { value: 'mission', label: 'Mission (prioritaire)' },
              { value: 'society', label: `Société${societyName ? ` · ${societyName}` : ''} (repli)` },
            ]}
          />
        </FieldV2>
      ) : (
        <InfoV2 tone="blue">
          {slot.allowMission
            ? 'Ce contact sera rattaché au niveau mission.'
            : `Ce contact sera rattaché au niveau société${
                societyName ? ` (${societyName})` : ''
              }, en repli pour les missions concernées.`}
        </InfoV2>
      )}

      {mode === 'search' && selected ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
          <ChipV2 label={`Sélection : ${contactLabel(selected)}`} tone="info" size="small" />
        </Box>
      ) : null}
    </DrawerV2>
  );
};

export default ContactAddDrawer;
